import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Eye, Stethoscope, FileText, Heart, TrendingUp } from 'lucide-react';

const GREEN_DOOR_LOGO = 'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

const groupViewsByViewer = (views) => {
  const order = [];
  const grouped = {};
  (views || []).forEach(v => {
    const key = v.viewer_name || 'Unknown';
    if (!grouped[key]) {
      order.push(key);
      grouped[key] = {
        viewer_name: v.viewer_name,
        employee_assocs: [...(v.employee_assocs || [])],
        key_non_employee_assocs: [...(v.key_non_employee_assocs || [])],
        has_funnel_signal: false,
        count: 0,
        individual_views: [],
      };
    }
    const g = grouped[key];
    g.count++;
    if (v.has_funnel_signal) g.has_funnel_signal = true;
    (v.key_non_employee_assocs || []).forEach(ka => { if (!g.key_non_employee_assocs.includes(ka)) g.key_non_employee_assocs.push(ka); });
    (v.employee_assocs || []).forEach(ea => { if (!g.employee_assocs.includes(ea)) g.employee_assocs.push(ea); });
    g.individual_views.push({
      view_datetime: v.view_datetime || v.card_completion_datetime || '',
      horses_on_card: v.horses_on_card,
      card_all_flags: v.card_all_flags || [],
      card_general_comments: v.card_general_comments || '',
      liked: v.liked || false,
      viewed_vet_report: v.viewed_vet_report || false,
      view_comments: v.view_comments || '',
    });
  });
  return order.map(k => grouped[k]);
};

/**
 * Shared Daily Sale Update document layout.
 * data     — full report data object { tenant_name, tenant_logo_url, sale_name, sale_company, sale_location, date, rows }
 * columns  — array of { key, label } for horse detail fields
 */
const DailySaleUpdateDocument = ({ data, columns = [], sectionOrder }) => {
  // Default: vetting before views
  const order = (sectionOrder && sectionOrder.length === 2) ? sectionOrder : ['vetting', 'views'];
  if (!data) return null;

  const colKeys = new Set(columns.map(c => c.key));
  // Card column visibility — default ON if not explicitly excluded
  const showHorsesOnCard   = !columns.length || colKeys.has('col_horses_on_card');
  const showViewNotes      = !columns.length || colKeys.has('col_view_notes');
  const showKeyAssocs      = !columns.length || colKeys.has('col_key_associations');
  // General card notes are always folded into the View Notes column (no separate column)

  return (
    <Card>
      <CardContent className="p-8 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            {data.tenant_logo_url
              ? <img src={data.tenant_logo_url} alt="Logo" className="h-16 mb-2 object-contain" />
              : <p className="font-serif text-2xl font-bold">{data.tenant_name}</p>}
          </div>
          <div className="text-right">
            <p className="font-serif text-3xl font-bold text-muted-foreground mb-1">DAILY SALE UPDATE</p>
            <p className="text-sm font-medium text-muted-foreground">{data.tenant_name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.date ? new Date(data.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}
            </p>
          </div>
        </div>

        {/* Sale Name + Company + Location + Account + Session — Result moves to horse strip */}
        <div className="border-t border-b py-3 flex justify-between items-start gap-4">
          <div>
            <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-0.5">Sale</p>
            <p className="text-xl font-semibold">{data.sale_name || '—'}</p>
            {(data.sale_company || data.sale_location) && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {[data.sale_company, data.sale_location].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          {(data.crm_entity_name || data.rows?.[0]?.session_name || data.rows?.[0]?.session_date) && (
            <div className="text-right text-sm space-y-1">
              {data.crm_entity_name && (
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Account </span>
                  <span className="font-medium">{data.crm_entity_name}</span>
                </div>
              )}
              {(data.rows?.[0]?.session_name || data.rows?.[0]?.session_date) && (
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Session </span>
                  <span className="font-medium">
                    {[data.rows[0].session_name, data.rows[0].session_date].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* One section per horse */}
        {(data.rows || []).length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No data found.</p>
        ) : (data.rows || []).map((row, idx) => (
          <div key={row.opportunity_id || idx} className={idx > 0 ? 'border-t pt-8' : ''}>
            {/* Horse Details Card */}
            <div className="bg-muted/30 rounded-lg p-5 mb-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {row.hip_number && <Badge variant="outline" className="text-base font-bold px-3 py-1">#{row.hip_number}</Badge>}
                <h3 className="font-serif text-2xl font-bold">{row.horse_name || '—'}</h3>
                {row.result !== undefined && (
                  <span className="ml-auto text-sm text-muted-foreground">
                    <span className="text-xs uppercase tracking-wide font-normal">Sale Result: </span>
                    <span className="font-semibold capitalize">{row.result || 'Pending'}</span>
                  </span>
                )}
              </div>
              {columns.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm mb-4">
                  {columns.map(f => {
                    const val = row[f.key];
                    if (!val && val !== 0) return null;
                    return (
                      <div key={f.key}>
                        <span className="text-xs text-muted-foreground block">{f.label}</span>
                        <span className="font-medium">{String(val)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {(row.owner_percentage || row.owners) && (
                <div className="border-t pt-2 text-sm text-muted-foreground">
                  {(row.owner_percentage ? row.owner_percentage.split('; ') : row.owners.split(', ')).join('  ·  ')}
                </div>
              )}
            </div>

            {/* Activity Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <Card><CardContent className="p-4 flex items-center gap-3">
                <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div><p className="text-xs text-muted-foreground">Views Today</p><p className="font-serif text-2xl font-semibold">{row.views_today ?? 0}</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3">
                <Eye className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div><p className="text-xs text-muted-foreground">Total Views</p><p className="font-serif text-2xl font-semibold">{row.total_views ?? 0}</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3">
                <Stethoscope className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div><p className="text-xs text-muted-foreground">Vetting Today</p><p className="font-serif text-2xl font-semibold">{row.vetting_today ?? 0}</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-4 flex items-center gap-3">
                <Stethoscope className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div><p className="text-xs text-muted-foreground">Total Vetting</p><p className="font-serif text-2xl font-semibold">{row.total_vetting ?? 0}</p></div>
              </CardContent></Card>
            </div>

            {/* Views and Vetting rendered in configurable order */}
            {order.map(section => section === 'views' ? (
              <div key="views" className="mb-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Views ({(row.view_details || []).length})
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-red-500 fill-current" /> Liked</span>
                    <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-blue-500" /> Viewed Vet Report</span>
                  </div>
                </div>
                {(row.view_details || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic pl-6">No completed views recorded</p>
                ) : (
                  <div className="border rounded-md overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-xs min-w-[140px]">Viewer</TableHead>
                          <TableHead className="text-xs text-center">Total Views</TableHead>
                          <TableHead className="text-xs min-w-[140px]">View Date / Time</TableHead>
                          {showHorsesOnCard && <TableHead className="text-xs text-center">Horses on Card</TableHead>}
                          <TableHead className="w-6"></TableHead>
                          <TableHead className="w-6"></TableHead>
                          {showViewNotes    && <TableHead className="text-xs min-w-[160px]">View Notes</TableHead>}
                          {showKeyAssocs    && <TableHead className="text-xs min-w-[120px]">Key Associations who Viewed or Vetted</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupViewsByViewer(row.view_details).map((v, vi) => (
                          <TableRow key={vi} className="hover:bg-muted/30">
                            <TableCell className="text-sm align-top p-0">
                              <div className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium">{v.viewer_name || '—'}</span>
                                  {v.has_funnel_signal && <TrendingUp className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" title="Funnel signal" />}
                                </div>
                                {(v.employee_assocs || []).map((ea, ei) => (
                                  <span key={ei} className="block text-xs text-muted-foreground">{ea}</span>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-center align-top p-0">
                              <div className="px-2 py-1.5">{v.count}</div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground p-0 align-top">
                              {v.individual_views.map((iv, i) => (
                                <div key={i} className={`px-3 py-1.5 ${i > 0 ? 'border-t border-muted/50' : ''}`}>
                                  {iv.view_datetime || '—'}
                                </div>
                              ))}
                            </TableCell>
                            {showHorsesOnCard && (
                            <TableCell className="text-xs text-muted-foreground text-center p-0 align-top">
                              {v.individual_views.map((iv, i) => (
                                <div key={i} className={`px-2 py-1.5 flex flex-wrap items-center justify-center gap-1 ${i > 0 ? 'border-t border-muted/50' : ''}`}>
                                  <span>{iv.horses_on_card != null ? iv.horses_on_card : '—'}</span>
                                  {(iv.card_all_flags || []).map((f, fi) => (
                                    <Badge key={fi} variant="outline" className="text-[10px] px-1 py-0 pointer-events-none">{f}</Badge>
                                  ))}
                                </div>
                              ))}
                            </TableCell>
                            )}
                            <TableCell className="w-6 text-center p-0 align-top">
                              {v.individual_views.map((iv, i) => (
                                <div key={i} className={`px-1 py-1.5 flex justify-center ${i > 0 ? 'border-t border-muted/50' : ''}`}>
                                  {iv.liked && <Heart className="w-3 h-3 text-red-500 fill-current" />}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell className="w-6 text-center p-0 align-top">
                              {v.individual_views.map((iv, i) => (
                                <div key={i} className={`px-1 py-1.5 flex justify-center ${i > 0 ? 'border-t border-muted/50' : ''}`}>
                                  {iv.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500" />}
                                </div>
                              ))}
                            </TableCell>
                            {showViewNotes && (
                            <TableCell className="text-xs p-0 align-top">
                              {v.individual_views.map((iv, i) => (
                                <div key={i} className={`px-3 py-1.5 ${i > 0 ? 'border-t border-muted/50' : ''}`}>
                                  {iv.view_comments && <div className="text-xs text-foreground">{iv.view_comments}</div>}
                                  {iv.view_comments && iv.card_general_comments && <div className="text-[10px] text-muted-foreground mt-0.5"><span className="font-medium">General Card Notes:</span> {iv.card_general_comments}</div>}
                                  {!iv.view_comments && iv.card_general_comments && <div className="text-[10px] text-muted-foreground"><span className="font-medium">General Card Notes:</span> {iv.card_general_comments}</div>}
                                  {!iv.view_comments && !iv.card_general_comments && <span className="text-foreground">—</span>}
                                </div>
                              ))}
                            </TableCell>
                            )}
                            {showKeyAssocs && (
                            <TableCell className="text-xs align-top p-0">
                              <div className="px-3 py-1.5">
                                {(v.key_non_employee_assocs || []).length === 0
                                  ? <span className="text-foreground">—</span>
                                  : (v.key_non_employee_assocs || []).map((ka, ki) => (
                                      <div key={ki} className="text-xs text-foreground leading-tight">{ka}</div>
                                    ))
                                }
                              </div>
                            </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ) : (
              <div key="vetting" className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Stethoscope className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Vetting Procedures ({(row.vetting_details || []).length})
                  </p>
                </div>
                {(row.vetting_details || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic pl-6">No vetting procedures recorded</p>
                ) : (
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-xs">Vet</TableHead>
                          <TableHead className="text-xs">Client</TableHead>
                          <TableHead className="text-xs">Date / Time</TableHead>
                          <TableHead className="text-xs">Procedure</TableHead>
                          <TableHead className="text-xs">Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(row.vetting_details || []).map((v, vi) => (
                          <TableRow key={vi} className="hover:bg-muted/30">
                            <TableCell className="text-sm align-top">
                              <span className="font-semibold">{v.vet_name || '—'}</span>
                              {v.vet_practice && <span className="block text-xs text-muted-foreground">{v.vet_practice}</span>}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{v.vet_client || '—'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{v.procedure_datetime || '—'}</TableCell>
                            <TableCell className="text-xs"><span className="font-medium">{v.procedure || '—'}</span></TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                {v.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                                <span>{v.notes || '—'}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Powered by Green Door */}
        <div className="pt-6 border-t flex justify-center">
          <img src={GREEN_DOOR_LOGO} alt="Powered by Green Door" className="w-1/4 mx-auto opacity-50" />
        </div>
      </CardContent>
    </Card>
  );
};

export default DailySaleUpdateDocument;
